import { getDataSource } from '@/lib/data-source';
import { ApiError } from '@/lib/data-source/types';

export type BillingCycleType = 'standard' | 'custom';
export type BillingInterval = 'day' | 'week' | 'month' | 'year';
export type UsageAggregation = 'period' | 'running' | 'none';

export interface BillingCycle {
  id: string;
  name?: string;
  months: number;
  type?: BillingCycleType;
  interval?: BillingInterval;
  interval_count?: number;
  intervalCount?: number;
  custom_duration_days?: number;
  customDurationDays?: number;
  display_name?: string;
  displayName?: string;
  description?: string;
  sort_order?: number;
  sortOrder?: number;
  is_default?: boolean;
  isDefault?: boolean;
  is_active?: boolean;
  isActive?: boolean;
  billing_anchor_day?: number;
  billingAnchorDay?: number;
  prorate_first_period?: boolean;
  prorateFirstPeriod?: boolean;
  allow_proration?: boolean;
  allowProration?: boolean;
  supports_trial?: boolean;
  supportsTrial?: boolean;
  default_trial_days?: number;
  defaultTrialDays?: number;
  usage_aggregation?: UsageAggregation;
  usageAggregation?: UsageAggregation;
  reset_usage_on_cycle?: boolean;
  resetUsageOnCycle?: boolean;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface BillingCycleCreateData {
  name: string;
  months: number;
  type?: BillingCycleType;
  interval: BillingInterval;
  interval_count?: number;
  custom_duration_days?: number;
  display_name: string;
  description?: string;
  sort_order?: number;
  is_default?: boolean;
  is_active?: boolean;
  billing_anchor_day?: number;
  prorate_first_period?: boolean;
  allow_proration?: boolean;
  supports_trial?: boolean;
  default_trial_days?: number;
  usage_aggregation?: UsageAggregation;
  reset_usage_on_cycle?: boolean;
}

export interface BillingCycleUpdateData extends Partial<BillingCycleCreateData> {}

export interface BillingCycleListResponse {
  data: BillingCycle[];
  jsonapi: {
    version: string;
  };
}

export interface BillingCycleResponse {
  data: BillingCycle;
  jsonapi: {
    version: string;
  };
}

function isNotFoundError(error: unknown): boolean {
  return Boolean(
    error &&
    typeof error === 'object' &&
    'status' in error &&
    (error as ApiError).status === 404
  );
}

class BillingCyclesService {
  private dataSource = getDataSource();

  private normalizeResponse(payload: BillingCycleResponse | BillingCycle): BillingCycle {
    if (
      payload &&
      typeof payload === 'object' &&
      'jsonapi' in payload &&
      'data' in payload &&
      payload.data &&
      typeof payload.data === 'object'
    ) {
      return payload.data as BillingCycle;
    }

    return payload as BillingCycle;
  }

  async getBillingCycles(productId: string): Promise<BillingCycle[]> {
    try {
      const response = await this.dataSource.get<BillingCycleListResponse>(
        `/api/v1/products/${productId}/billing/billing-cycles`
      );
      return response.data.data;
    } catch (error) {
      if (isNotFoundError(error)) {
        console.warn(`No billing cycles found for product ${productId}, returning empty list.`);
        return [];
      }
      console.error(`Error fetching billing cycles for product ${productId}:`, error);
      throw error;
    }
  }

  async getBillingCycle(productId: string, cycleId: string): Promise<BillingCycle | null> {
    try {
      const response = await this.dataSource.get<BillingCycleResponse>(
        `/api/v1/products/${productId}/billing/billing-cycles/${cycleId}`
      );
      return this.normalizeResponse(response.data as BillingCycleResponse | BillingCycle);
    } catch (error) {
      if (isNotFoundError(error)) {
        console.warn(`Billing cycle ${cycleId} not found for product ${productId}.`);
        return null;
      }
      console.error(`Error fetching billing cycle ${cycleId}:`, error);
      throw error;
    }
  }

  async createBillingCycle(
    productId: string,
    data: BillingCycleCreateData
  ): Promise<BillingCycle> {
    try {
      const requestBody = {
        data: {
          type: 'billing-cycles',
          attributes: {
            name: data.name,
            months: data.months,
            type: data.type || 'standard',
            interval: data.interval,
            'interval-count': data.interval_count || 1,
            'custom-duration-days': data.custom_duration_days,
            'display-name': data.display_name,
            description: data.description,
            'sort-order': data.sort_order || 1,
            'is-default': data.is_default || false,
            'is-active': data.is_active ?? true,
            'billing-anchor-day': data.billing_anchor_day,
            'prorate-first-period': data.prorate_first_period || false,
            'allow-proration': data.allow_proration ?? true,
            'supports-trial': data.supports_trial ?? true,
            'default-trial-days': data.default_trial_days,
            'usage-aggregation': data.usage_aggregation || 'period',
            'reset-usage-on-cycle': data.reset_usage_on_cycle ?? true
          }
        }
      };

      const response = await this.dataSource.post<BillingCycleResponse>(
        `/api/v1/products/${productId}/billing/billing-cycles`,
        requestBody
      );
      return this.normalizeResponse(response.data as BillingCycleResponse | BillingCycle);
    } catch (error) {
      console.error(`Error creating billing cycle for product ${productId}:`, error);
      throw error;
    }
  }

  async updateBillingCycle(
    productId: string,
    cycleId: string,
    data: BillingCycleUpdateData
  ): Promise<BillingCycle> {
    try {
      const requestBody = {
        data: {
          type: 'billing-cycles',
          id: cycleId,
          attributes: {
            ...(data.name && { name: data.name }),
            ...(data.months !== undefined && { months: data.months }),
            ...(data.type && { type: data.type }),
            ...(data.interval && { interval: data.interval }),
            ...(data.interval_count !== undefined && { 'interval-count': data.interval_count }),
            ...(data.custom_duration_days !== undefined && { 'custom-duration-days': data.custom_duration_days }),
            ...(data.display_name && { 'display-name': data.display_name }),
            ...(data.description !== undefined && { description: data.description }),
            ...(data.sort_order !== undefined && { 'sort-order': data.sort_order }),
            ...(data.is_default !== undefined && { 'is-default': data.is_default }),
            ...(data.is_active !== undefined && { 'is-active': data.is_active }),
            ...(data.billing_anchor_day !== undefined && { 'billing-anchor-day': data.billing_anchor_day }),
            ...(data.prorate_first_period !== undefined && { 'prorate-first-period': data.prorate_first_period }),
            ...(data.allow_proration !== undefined && { 'allow-proration': data.allow_proration }),
            ...(data.supports_trial !== undefined && { 'supports-trial': data.supports_trial }),
            ...(data.default_trial_days !== undefined && { 'default-trial-days': data.default_trial_days }),
            ...(data.usage_aggregation && { 'usage-aggregation': data.usage_aggregation }),
            ...(data.reset_usage_on_cycle !== undefined && { 'reset-usage-on-cycle': data.reset_usage_on_cycle })
          }
        }
      };

      const response = await this.dataSource.patch<BillingCycleResponse>(
        `/api/v1/products/${productId}/billing/billing-cycles/${cycleId}`,
        requestBody
      );
      return this.normalizeResponse(response.data as BillingCycleResponse | BillingCycle);
    } catch (error) {
      console.error(`Error updating billing cycle ${cycleId}:`, error);
      throw error;
    }
  }

  async deleteBillingCycle(productId: string, cycleId: string): Promise<void> {
    try {
      await this.dataSource.delete(
        `/api/v1/products/${productId}/billing/billing-cycles/${cycleId}`
      );
    } catch (error) {
      console.error(`Error deleting billing cycle ${cycleId}:`, error);
      throw error;
    }
  }
}

export const billingCyclesService = new BillingCyclesService();
